import type { AnalyticsPeriod } from "./period-selector"

/**
 * period-range — translate a named AnalyticsPeriod into the explicit
 * (start, end) ISO-date window the backend summary endpoints require. This is
 * the only place the three workspaces agree on what "Week" / "Month" mean, so
 * both analytics pages produce identical ranges for the same selection.
 *
 * Windows are inclusive and anchored to the CLIENT-LOCAL day (the operator's
 * "today"), matching how the operator dashboard captures `new Date()`:
 *   - today → [today, today]            (1 day)
 *   - week  → [today − 6 days, today]   (7 days, incl. today)
 *   - month → [today − 29 days, today]  (30 days, incl. today)
 *
 * Dates are formatted as YYYY-MM-DD from the local calendar fields (NOT
 * toISOString(), which would shift to UTC and can roll the date across
 * midnight in negative-offset timezones).
 */

export interface DateRange {
  start: string
  end: string
}

/** Days spanned by each window (inclusive of today). */
const PERIOD_DAYS: Record<AnalyticsPeriod, number> = {
  today: 1,
  week: 7,
  month: 30,
}

/** Local-calendar YYYY-MM-DD (no UTC shift). */
function toLocalISODate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/** Inclusive [start, end] window for a period, anchored at `now` (default today). */
export function rangeForPeriod(
  period: AnalyticsPeriod,
  now: Date = new Date(),
): DateRange {
  const end = new Date(now)
  const start = new Date(now)
  // Inclusive of today, so a span of N days steps back N − 1.
  start.setDate(start.getDate() - (PERIOD_DAYS[period] - 1))
  return { start: toLocalISODate(start), end: toLocalISODate(end) }
}

/** Number of days the window covers (used for Avg Revenue/Day). */
export function daysInPeriod(period: AnalyticsPeriod): number {
  return PERIOD_DAYS[period]
}
